import React, { ReactNode } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@src/components/base'
import { CardProps } from './types'

interface CardContainerProps extends CardProps {
  icon: ReactNode
  title: string
  description?: string
  body?: ReactNode
  buttonTitle?: string
  badgeAccessory?: ReactNode
  children?: ReactNode
  onCardPress?: () => void
  variant?: 'default' | 'chatbot'
}

export const Card = ({
  icon,
  title,
  description,
  body,
  buttonTitle,
  badgeAccessory,
  children,
  onCardPress,
  onButtonPress,
  variant = 'default',
}: CardContainerProps) => {
  const styles = useStyleSheet(themedStyles)

  return (
    <TouchableOpacity
      accessibilityLabel={title}
      style={variant === 'default' ? styles.card : styles.chatbotCard}
      disabled={!onCardPress}
      onPress={onCardPress}
    >
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>{icon}</View>
        <View style={styles.content}>
          <View style={styles.titleContainer}>
            <Text type="large" lineSpacing="tight" bold style={styles.title}>
              {title}
            </Text>
            {badgeAccessory}
          </View>
          <View style={onCardPress ? styles.body : undefined}>
            {body}
            {!body && !!description && (
              <Text type="regular" lineSpacing="tight" style={styles.description}>
                {description}
              </Text>
            )}
          </View>
        </View>
        {!!onCardPress && (
          <Icon name="caret-right" size="16" weight="bold" style={styles.accessoryRight} />
        )}
      </View>

      {(!!children || !!onButtonPress) && <View style={styles.divider} />}

      {children}

      {!!onButtonPress && !!buttonTitle && (
        <Button
          type="primary"
          size="block"
          accessibilityLabel={buttonTitle}
          onPress={onButtonPress}
        >
          {buttonTitle}
        </Button>
      )}
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  card: {
    gap: 16,
    borderRadius: 16,
    padding: 16,
    backgroundColor: 'theme.surface.base3',
  },
  chatbotCard: {
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    borderRadius: 24,
    backgroundColor: 'theme.background',
    padding: 16,
  },
  contentContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  iconContainer: {
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: 'theme.surface.base',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 12,
    width: 64,
    height: 68,
    overflow: 'hidden',
  },
  content: {
    gap: 2,
    flexGrow: 1,
    flexShrink: 1,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  title: {
    flexShrink: 1,
  },
  body: {
    paddingRight: 16,
  },
  description: {
    marginTop: 4,
  },
  divider: {
    height: 1,
    backgroundColor: 'theme.surface.base1',
  },
  accessoryRight: {
    position: 'absolute',
    top: '50%',
    right: 0,
    color: 'theme.primary.base',
  },
})
